import { Modal } from 'bootstrap.native';
import { utils } from '../../../Foundation';

export class AmendPrices {
    /**
    * Logic for validating inputs eg. when CC wants to update prices for the modal
    * @param {HTMLElement} element - Modal Element
    */
    constructor (element) {
        // Name, reference
        element.AmendPrices = this;
        
        // Props
        this._props = {
            validated: false,
            onValidateClick: this._onValidateClick.bind(this)
        };

        // Elements
        this._refs = {
            el: element,
            groups: Array.from(element.querySelectorAll('.form-group')),
            inputs: Array.from(element.querySelectorAll('input[type="text"]')),
            alert: element.querySelector('.alert'),
            buttons: {
                cancel: element.querySelector('[data-dismiss="modal"]'),
                validate: element.querySelector('.btn-validate-inputs'),
                save: element.querySelector('.btn-save')
            }
        };

        // Event subscribtions
        this._refs.el.addEventListener('hidden.bs.modal', this._onModalHide.bind(this), false);
        this._refs.el.addEventListener('show.bs.modal', this._onModalShow.bind(this), false);
        this._refs.el.addEventListener('keydown', this._updateOnKeyInput.bind(this));
        this._refs.buttons.validate.addEventListener('click', this._props.onValidateClick);
        this._refs.buttons.save.addEventListener('click', () => {
            const oModal = this._refs.el.Modal || new Modal(this._refs.el);
            oModal.hide();
        });
    } 

    /**
     * Method for handling initial fields-handling once modal is shown
     */
    _onModalShow () {
        this._fields.forEach(x => {
            const value = x.value.trim();

            if (x.disabled) {
                x.classList.add('is-disabled');
            }

            if (value.length) {
                x.setAttribute('data-value', value);
            }
        });
    }

    /**
     * Method for handling closure of modal in any given step of userflow
     * @param {Event} e - Event interface
     */
    _onModalHide (e) {
        // Reset styling on error fields
        const invalidFields = this._refs.el.querySelectorAll('input[type="text"].invalid');
        invalidFields.forEach(field => field.classList.remove('invalid'));

        let oTarget = e.target,
            oModal = this._refs.el.Modal || new Modal(this._refs.el);

        // On close, empty all fields thats not disabled
        const arrFields = Array.from(this._refs.el.querySelectorAll('input[type="text"]:not(.aspNetDisabled):not(.is-disabled)'));
        arrFields.forEach(x => {
            const value = x.value.trim();

            // State mangement
            x.disabled = false;

            // Value mangement
            if (x.getAttribute('data-value')) {
                x.value = x.getAttribute('data-value');
            } else {
                x.value = '';
            }
        });

        // Reset event listener
        this._refs.buttons.cancel.removeEventListener('click', oModal.hide());
        this._refs.buttons.validate.removeEventListener('click', this._props.onValidateClick);

        // Rset validate/save button to inital state
        this._refs.buttons.validate.classList.remove('is-hidden');
        this._refs.buttons.save.classList.add('is-hidden');
        this._refs.alert.classList.add('is-hidden');

        e.preventDefault();
    }

    /**
     * Validate and execute step-handling based on validation flow
     * @param {Event} e - Event Interface
     */
    _onValidateClick(e) {
        e.preventDefault();

        if (this._validation()) {
            // Hide alert
            this._refs.alert.classList.add('is-hidden');

            // Lock fields from editing
            this._fields.forEach(x => x.disabled = true);

            // Set button states
            this._refs.buttons.validate.classList.add('is-hidden');
            this._refs.buttons.save.classList.remove('is-hidden');
            this._refs.buttons.save.focus();
        } else {
            // Show alert 
            this._refs.alert.classList.remove('is-hidden');

            // unlock fields from editing
            this._fields.forEach(x => x.disabled = false);

            // Set button states
            this._refs.buttons.validate.classList.remove('is-hidden');
            this._refs.buttons.save.classList.add('is-hidden');
        }

        return false;
    }

    _updateOnKeyInput(e) {
        if(e.keyCode === 13) {
            e.preventDefault();
            const validateBtn = this._refs.el.querySelector('.btn-validate-inputs');
            const saveBtn = this._refs.el.querySelector('.btn-save');
            if(validateBtn.className.includes('is-hidden')) {
                saveBtn.click();
                return;
            }
            validateBtn.click();
            return;
        }
        
        // Check if input fields are validated
        const getInvalidFields = this._validateAllInputFields();
        
        // Modal contains invalid fields
        if(e.target.className.includes('invalid')) {
            // reset invalid fields
            let arrFormGroups = Array.from(this._refs.el.querySelectorAll('.form-group:not(.is-disabled)'));
            arrFormGroups.forEach(group => group.querySelectorAll('input').forEach(e => e.classList.remove('invalid')));
            // Apply invalid fields
            if (getInvalidFields.length > 0) { 
                getInvalidFields.forEach(elem => {
                    elem.classList.add('invalid');
                })
            }
        }
    }

    /**
     * Get all input fields within element
     * @returns {Array}
     **/
    get _fields() {
        return this._refs.inputs.filter(x => !x.disabled);
    }

    /**
     * Validate current state of fields
     * @returns {Boolean} 
     **/
    _validation() {
        let arrFormGroups = Array.from(this._refs.el.querySelectorAll('.form-group:not(.is-disabled)')),
            isGroupValidated = 0;

        // Get list of input Forms
        arrFormGroups.forEach(group => {
            // Check if input fields are validated
            const getInvalidFields = this._validateAllInputFields();
        
            // Validate if group is set as validated
            // OR if all fields are empty
            if (getInvalidFields.length === 0) {
                this._validatePriceInput(group);
                group.classList.add('is-valid');
                isGroupValidated++;
            } else {
                getInvalidFields[0].focus();
                getInvalidFields.forEach(elem => {
                    elem.classList.add('invalid')
                })
                group.classList.remove('is-valid');
                isGroupValidated--;
            }
        });

        return arrFormGroups.length === isGroupValidated;
    }

    // remove dot or comma in thousand separator (Deprecated - No thousand separator)
    _validatePriceInput(group) {
        const priceInput = group.querySelector('[data-filter="currency"]');
        if (!utils.validateCurrencyField(priceInput)) {
            this.oldValue = priceInput.value;
            const commaPosition = this.oldValue.indexOf(',');
            const dotPosition = this.oldValue.indexOf('.');
            // remove dot thousand separator
            if(commaPosition > dotPosition) {
                return priceInput.value = this.oldValue.replace(/./g,'')
            }
            // remove comma thousand separator
            if(commaPosition < dotPosition) {
                return priceInput.value = this.oldValue.replace(/,/g,'')
            }
        }
    }

    // validate all input field 
    _validateAllInputFields() {
        const arrFormGroups = Array.from(this._refs.el.querySelectorAll('.form-group:not(.is-disabled)'));
        // Check input field currency in all groups 
        let allGroups = [];

        arrFormGroups.forEach(group => {
            // Get text input field that are not disabled
            const arrInputFields = Array.from(group.querySelectorAll('input[type="text"]:not(:disabled)'));
            // Set focus on the first invalid input field
            const checkGroup = arrInputFields.filter(x => x.value.trim().length > 0);
            // if not all fields are input or not valid
            const listOfFails = arrInputFields.filter(x => {
                if(checkGroup.length > 0) {
                    return x.value.trim().length < 1 || !x.checkValidity();
                }
            });
            // If currency field includes text
            const currencyFails = arrInputFields
                .filter(x => {
                    const isCurrencyField = x.hasAttribute('data-filter');
                    if(isCurrencyField) {
                        return !utils.validateCurrencyField(x);
                    }
                });
            allGroups = [...allGroups, ...currencyFails, ...listOfFails];
        })
        return allGroups;
    }
}